import SidebarLink, { SidebarLinkProps } from '@/components/shared/Sidebar/SidebarLink';
import { linkWithoutBGStyles } from '@/components/shared/Sidebar/styles';
import StyledLink from '@/components/styled/StyledLink';
import { SvgIcon, Typography } from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Box from '@mui/system/Box';
import { useSidebarNavigation } from '@/contexts/sidebarNavigationContext';
import React, { useEffect, useRef, useState, FunctionComponent, SVGProps } from 'react';
import { SvgIconComponent } from '@mui/icons-material';

interface SidebarLinkSetProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  id: any;
  title: string;
  icon: FunctionComponent<SVGProps<SVGSVGElement>> | SvgIconComponent;
  links: Array<SidebarLinkProps>;
  open: boolean;
  anchorEl: HTMLElement | null;
  onTriggerClick: React.MouseEventHandler<HTMLElement>;
  isVisible?: boolean;
}

const SidebarLinkSet: React.FC<SidebarLinkSetProps> = ({
  id,
  title,
  icon,
  links,
  open,
  onTriggerClick,
}) => {
  const { isHovered } = useSidebarNavigation();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  // Toggle function for the StyledLink.tsx click
  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Effect for handling clicks outside the component
  useEffect(() => {
    function handleClickOutside(event: Event) {
      // @ts-expect-error: Property 'contains' does not exist on type 'never'.
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false); // Close the sidebar if click is outside
      }
    }

    // Add when the component mounts
    document.addEventListener('mousedown', handleClickOutside);
    // Remove event listener on cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [containerRef]);

  // Close navs on losing hover of the sidebar.
  useEffect(() => {
    !isHovered && setIsOpen(false);
  }, [isHovered]);

  return (
    <Box
      ref={containerRef}
      onClick={handleToggle}
      sx={{
        ...linkWithoutBGStyles,
        ...{
          padding: '10px 8px',
          position: 'relative',
        },
      }}
    >
      {/* @ts-expect-error: to prop. */}
      <StyledLink aria-describedby={id} type='button' onClick={onTriggerClick}>
        <SvgIcon component={icon} inheritViewBox sx={{ color: 'typography.main' }} />
        <Typography className='sidebar__linkset__title' sx={{ color: 'typography.main' }}>
          {title}
        </Typography>
        <Box sx={{ position: 'absolute', right: '11px' }}>
          {open ? <ArrowRightIcon color='inherit' /> : <ArrowDropDownIcon color='inherit' />}
        </Box>
      </StyledLink>

      <Box
        sx={{
          position: 'absolute',
          backgroundColor: 'primary.main',
          color: 'inherit',
          border: 0,
          left: 'calc(100% + 5px)',
          transform: 'translateY(-50%)',
          top: '50%',
          overflow: 'hidden',
          transition: isOpen ? 'width .3s ease-in' : 'width .3s ease-out',
          width: isOpen ? '180px' : 0,
          zIndex: 1300,
          '& .MuiTypography-root': {
            fontSize: '12px !important',
          },
        }}
      >
        {links.map(
          ({ to, handleClick, icon, text, boxContainerStyles, target, subMenu }, index) => (
            <SidebarLink
              subMenu={subMenu}
              to={to}
              target={target}
              key={index}
              handleClick={handleClick}
              icon={icon}
              text={text}
              boxContainerStyles={boxContainerStyles}
            />
          ),
        )}
      </Box>
    </Box>
  );
};

export default SidebarLinkSet;
